import { CheckboxStatus, SelectItemV2, UNIT_DEFAULTS } from '@schenck/rotec-components';
import {
  FilterQualityTypes,
  PositioningMode,
  ReadoutRetentionType,
  RotorTypeSelections,
  ToleranceModes,
  TolerancePlanePositions,
} from 'src/utils/enums/rotor-type.enum';
import { Machine } from 'src/utils/interfaces/machine.interfaces';
import {
  CalibrationsAndCompensationsFormData,
  CorrectionFormData,
  CorrectionLocationsFormData,
  CorrectionLocationsSideFormData,
  CorrectionMethodFormData,
  MeasurementSettingsFormData,
  RotorTypeFormsData,
  RotorTypeSettingsFormData,
  SummaryFormData,
  SupportSettingsFormData,
  ToleranceIsoCalculationFormData,
  TolerancesFormData,
  ToleranceUserDefinedFormData,
} from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import { Attachment } from 'src/utils/interfaces/rotor-type.interfaces';
import {
  TOLERANCES_CALCULATIONS,
  TOLERANCES_UNITS,
} from '../steps/tolerances-step/tolerances-step-const';

export const RotorTypeDetailsStoreName = 'RotorTypeDetails';

export class SupportSettingsStepFormData implements SupportSettingsFormData {
  selectedCorrectionPlane: RotorTypeSelections = null;
  laserLinesActive: boolean = null;
}

export class RotorTypeSettingsStepFormData implements RotorTypeSettingsFormData {
  rotorTypeName: string = null;
  rotorTypeDescription: string = null;
  rotateDirection: number = 0;
  a: SelectItemV2 = { name: 'a', value: null, unit: UNIT_DEFAULTS.length };
  b: SelectItemV2 = { name: 'b', value: null, unit: UNIT_DEFAULTS.length };
  c: SelectItemV2 = { name: 'c', value: null, unit: UNIT_DEFAULTS.length };
  r1: SelectItemV2 = { name: 'r1', value: null, unit: UNIT_DEFAULTS.length };
  r2: SelectItemV2 = { name: 'r2', value: null, unit: UNIT_DEFAULTS.length };
  pedestalPosition1: SelectItemV2 = {
    name: 'pedestalPosition1',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  pedestalPosition2: SelectItemV2 = {
    name: 'pedestalPosition2',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  balancingSpeed: SelectItemV2 = {
    name: 'balancingSpeed',
    value: null,
    unit: UNIT_DEFAULTS.frequency,
  };
}

export class MeasurementSettingsStepFormData implements MeasurementSettingsFormData {
  filterDelay = {
    name: 'filterDelay',
    value: 0,
    unit: 'sec',
  };
  filterTime = {
    name: 'filterTime',
    value: null,
    unit: 'sec',
  };
  filterQuality = FilterQualityTypes.Standard;
  readoutRetention = ReadoutRetentionType.Automatic;
  positioningMode = PositioningMode.Automatic;
  positioningAngle = {
    name: 'positioningAngle',
    value: 2,
    unit: '°',
  };
  enableAverageOverTime = false;
  averageTime = {
    name: 'averageTime',
    value: null,
    unit: 'sec',
  };
}

export class CorrectionMethodStepFormData implements CorrectionMethodFormData {
  left = new CorrectionMethodSide();
  right = new CorrectionMethodSide();
}

export class CorrectionLocationsStepFormData implements CorrectionLocationsSideFormData {
  left = new CorrectionLocationsSide();
  right = new CorrectionLocationsSide();
}

export class CorrectionLocationsSide implements CorrectionLocationsFormData {
  locationType = 'Free';
  locations = [];
  sectors = [];
  hasRadiusPerPlace = false;
}

export class CorrectionMethodSide implements CorrectionFormData {
  image: null;
  settings: null;
  description: null;
  locations: null;
  drillDirectionNumber: {
    name: null;
    value: null;
    unit: null;
  };
  drillDiameter: null;
  drillDirectionSelection: null;
  drillTip: 1;
  drillTipAngle: 118;
  centerTipHeight: null;
  centerTipRadius: null;
  maximumDrillDepth: null;
  minimumDrillHoleAngleDist: null;
  materialDensity: null;
  crossEdgeFactor: null;
  correctionSpacing: null;
  weightsPerLocation: null;
  addMass: null;
  balancingDisplay: null;
  weights: null;
}

export class TolerancesStepFormData implements TolerancesFormData {
  userDefined = new UserDefinedFormData();
  isoCalculation = new IsoCalculationFormData();
  toleranceMode: SelectItemV2 = {
    name: 'User Defined',
    value: ToleranceModes.Manual,
  };
}

export class UserDefinedFormData implements ToleranceUserDefinedFormData {
  selectedStaticUnit: SelectItemV2 = TOLERANCES_UNITS[0];
  selectedCoupleUnit: SelectItemV2 = TOLERANCES_UNITS[0];
  selectedDynamicUnit: SelectItemV2 = TOLERANCES_UNITS[0];
  selectedCalculation: SelectItemV2 = TOLERANCES_CALCULATIONS[0];
  dynamicTolerance1: SelectItemV2 = {
    name: 'dynamicTolerance1',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'unbalance'}`],
  };
  dynamicTolerance2: SelectItemV2 = {
    name: 'dynamicTolerance2',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'unbalance'}`],
  };
  staticTolerance: SelectItemV2 = {
    name: 'staticTolerance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'unbalance'}`],
  };
  coupleTolerance: SelectItemV2 = {
    name: 'coupleTolerance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'unbalance'}`],
  };
  selectedTolerancePlane: SelectItemV2 = {
    name: 'selectedTolerancePlane',
    value: TolerancePlanePositions.Correction,
  };
  tolerancePlane1Distance: SelectItemV2 = {
    name: 'tolerancePlane1Distance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'length'}`],
  };
  tolerancePlane2Distance: SelectItemV2 = {
    name: 'tolerancePlane2Distance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'length'}`],
  };
  constructor(private selectedUnit?: string) {}
}

export class IsoCalculationFormData implements ToleranceIsoCalculationFormData {
  selectedStaticUnit: SelectItemV2 = TOLERANCES_UNITS[0];
  selectedDynamicUnit: SelectItemV2 = TOLERANCES_UNITS[0];
  selectedUnbalanceUnit: SelectItemV2 = {
    name: 'selectedUnbalanceUnit',
    value: UNIT_DEFAULTS.unbalance,
    unit: UNIT_DEFAULTS.unbalance,
  };
  selectedCalculation: SelectItemV2 = TOLERANCES_CALCULATIONS[0];
  centerGravity1: SelectItemV2 = {
    name: 'centerGravity1',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  centerGravity2: SelectItemV2 = {
    name: 'centerGravity2',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  centerGravity: SelectItemV2 = {
    name: 'centerGravity',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  dynamicTolerance1: SelectItemV2 = {
    name: 'dynamicTolerance1',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  };
  dynamicTolerance2: SelectItemV2 = {
    name: 'dynamicTolerance2',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  };
  staticTolerance: SelectItemV2 = {
    name: 'staticTolerance',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  };
  rotorMass: SelectItemV2 = {
    name: 'rotorMass',
    value: null,
    unit: UNIT_DEFAULTS.mass,
  };
  nominalSpeed: SelectItemV2 = {
    name: 'nominalSpeed',
    value: null,
    unit: UNIT_DEFAULTS.frequency,
  };
  balancingQualityGrade: SelectItemV2 = {
    name: 'G6,3',
    value: '6,3',
    unit: null,
  };
  rollerBedDistanceInput = {
    name: 'rollerBedDistanceInput',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  rollerBedDistance = {
    name: 'rollerBedDistance',
    value: null,
    unit: UNIT_DEFAULTS.length,
  };
  deviation = 0;
  selectedTolerancePlane = {
    name: 'selectedTolerancePlane',
    value: TolerancePlanePositions.Correction,
  };
  tolerancePlane1Distance: SelectItemV2 = {
    name: 'tolerancePlane1Distance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'length'}`],
  };
  tolerancePlane2Distance: SelectItemV2 = {
    name: 'tolerancePlane2Distance',
    value: null,
    unit: UNIT_DEFAULTS[`${this.selectedUnit || 'length'}`],
  };
  constructor(private selectedUnit?: string) {}
}

export class SummaryStepFormData implements SummaryFormData {
  selectedMachine: Machine = null;
  photo: string = null;
  attachments: Attachment[] = null;
  cloudSync: boolean = false;
  syncStatus: string = null;
  syncStatusMetaInfo: string = null;
  syncValidFrom: string = null;
}

export interface RotorTypeDetailsStore {
  forms: RotorTypeFormsData;
}

export class CalibrationsStepFormData implements CalibrationsAndCompensationsFormData {
  keyCompensationToggle: boolean = true;
  keyCompensationCheckbox: CheckboxStatus = 'unchecked';
  biasCompensationToggle: boolean = true;
  biasCompensationCheckbox: CheckboxStatus = 'unchecked';
  indexCompensationToggle: boolean = true;
  indexCompensationCheckbox: CheckboxStatus = 'unchecked';
  unbalanceCalibrationToggle: boolean = true;
  unbalanceCalibrationCheckbox: CheckboxStatus = 'unchecked';
}
